// apps/web/src/pages/author/constraint-config/ConflictBadge.tsx
//
// Phase 9 Plan 10 Wave-4 — inline WINNING / SUPERSEDED badge for the
// Constraints section (CSTR-04 first-wins surface + CSTR-05 explanation
// threading).
//
// UI-SPEC §D (lines 320-331) color contract:
//   WINNING    → bg-secondary text-foreground border border-border
//   SUPERSEDED → bg-muted text-muted-foreground border border-input
// NO --destructive token — a conflict is not an error; first-wins is the
// policy. Tooltip on hover/focus explains the policy + cites the winning
// constraint position (UI-SPEC line 462).
//
// Label typography: text-xs font-semibold uppercase tracking-wider.
// State machine:
//   - winning  → static label
//   - superseded → click-target that calls onLocate(); the host (Constraints
//     section) scrolls + focuses the winning row.
//
// The component is rendered INLINE inside the row body, NOT a full-width
// banner. The cycle banner (CycleErrorBanner.tsx) is the destructive case;
// this component is informational.

import * as React from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export interface ConflictBadgeProps {
  /** Visual + behavioral variant. */
  state: "winning" | "superseded";
  /** Number of constraints below the winning one that are superseded by it.
   * Only consulted when state="winning". Tooltip surfaces this count. */
  supersededCount?: number;
  /** Kind of the winning constraint (e.g. "visibility"). Surfaced in the
   * SUPERSEDED tooltip per UI-SPEC line 462. */
  winningKind?: string;
  /** Zero-based document position of the winning constraint. Surfaced in
   * the SUPERSEDED tooltip as "#{winningIndex + 1}". */
  winningIndex?: number;
  /** Click handler for SUPERSEDED variant — host scrolls + focuses the
   * winning row. Inert on the WINNING variant. */
  onLocate?: () => void;
}

const BADGE_BASE =
  "inline-flex items-center justify-center rounded-md px-2 py-0.5 text-xs font-semibold uppercase tracking-wider focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

const WINNING_CLASSES = "bg-secondary text-foreground border border-border";
const SUPERSEDED_CLASSES = "bg-muted text-muted-foreground border border-input cursor-pointer hover:opacity-80";

export function ConflictBadge({
  state,
  supersededCount,
  winningKind,
  winningIndex,
  onLocate,
}: ConflictBadgeProps): React.ReactElement {
  if (state === "winning") {
    const count = supersededCount ?? 0;
    const tooltipText = `First-wins conflict policy: this constraint resolves first. ${count} constraint(s) below it are superseded.`;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={cn(BADGE_BASE, WINNING_CLASSES)} data-testid="conflict-badge-winning">
            WINNING
          </span>
        </TooltipTrigger>
        <TooltipContent>{tooltipText}</TooltipContent>
      </Tooltip>
    );
  }

  // state === "superseded"
  const idx = winningIndex ?? 0;
  const kind = winningKind ?? "rule";
  const tooltipText = `Superseded by constraint #${idx + 1} (${kind}). This rule is parked.`;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onLocate}
          className={cn(BADGE_BASE, SUPERSEDED_CLASSES)}
          data-testid="conflict-badge-superseded"
        >
          SUPERSEDED — View winning rule
        </button>
      </TooltipTrigger>
      <TooltipContent>{tooltipText}</TooltipContent>
    </Tooltip>
  );
}
ConflictBadge.displayName = "ConflictBadge";
